import React, {Component, ErrorInfo, ReactNode} from 'react';
import {Button, Headline, Body} from '@lightricks/react-design-system';
import Sentry from '@/services/sentry/Sentry';
import ContactUsButton from '@/components/contact-us-button/ContactUsButton';
import translate from '@/utils/translate';
import useGenericButtonPressedEvent from '@/hooks/analytics/use-generic-button-pressed-event/useGenericButtonPressedEvent';
import Delta from '@/utils/wrappers/Delta';
import DeltaHelper from '@/services/delta/DeltaHelper';
import styles from './ErrorBoundary.module.scss';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  fallback?: ReactNode;
}

interface InternalProps extends Props {
  /**
   * Callback to track analytics when the "Log In Again" button is pressed.
   * Only used for the default fallback UI (when no custom fallback is provided).
   */
  onButtonPressed?: (buttonName: string, screenName: string) => void;
}

interface State {
  hasError: boolean;
}

/**
 * Error Boundary component to catch React rendering errors
 * Prevents white screen of death by showing a fallback UI
 *
 * NOTE: Error Boundaries MUST be class components in React.
 * React's error boundary API (componentDidCatch, getDerivedStateFromError)
 * is only available in class components. There's no hook equivalent.
 * This is a React limitation, not a design choice.
 */
class ErrorBoundary extends Component<InternalProps, State> {
  constructor(props: InternalProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(): State {
    return {
      hasError: true,
    };
  }

  componentDidUpdate(prevProps: InternalProps, prevState: State) {
    // Send screen presented event when error UI is first shown
    const {hasError} = this.state;
    if (!prevState.hasError && hasError) {
      const location = window.location.pathname || '';
      DeltaHelper.screenPresented(location, 'Error Boundary', {
        error_boundary: true,
      });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry
    Sentry.captureException(error, {
      title: 'React Error Boundary caught an error',
      extra: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      },
    });

    // Call custom error handler if provided
    const {onError} = this.props;
    if (onError) {
      onError(error, errorInfo);
    }
  }

  handleReset = async () => {
    // Track analytics for "Log In Again" button press
    const {onButtonPressed} = this.props;
    if (onButtonPressed) {
      onButtonPressed('Log In Again', 'Error Boundary');
    }

    try {
      // Flush analytics before clearing storage and reloading
      await Delta.flushEvents();

      // Clear all localStorage
      localStorage.clear();

      // Clear all sessionStorage
      sessionStorage.clear();

      // Clear all cookies
      // Cookies must be cleared by setting them to expire in the past
      const cookies = document.cookie.split(';');
      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf('=');
        const name =
          eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
        // Clear cookie for current domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        // Also try to clear for parent domain
        const domain = window.location.hostname.split('.').slice(-2).join('.');
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
      });

      // Reload the page to start fresh
      window.location.reload();
    } catch (e) {
      // If clearing fails, just reload anyway
      console.error('Failed to clear storage:', e);
      window.location.reload();
    }
  };

  renderDefaultFallback = () => {
    return (
      <div className={styles.container}>
        <div className={styles.contentWrapper}>
          <Headline size="xl" className={styles.title}>
            {translate('Something went wrong')}
          </Headline>
          <Body size="lg" className={styles.message}>
            {translate('Please log back in to continue.')}
          </Body>
          <Button
            onClick={this.handleReset}
            appearance="brand"
            size="large"
            mode="filled"
          >
            {translate('Log In Again')}
          </Button>
          <ContactUsButton
            screen_name="Error Boundary"
            buttonText={translate('Contact us')}
            textColor="secondary"
          />
        </div>
      </div>
    );
  };

  render() {
    const {hasError} = this.state;
    const {children, fallback} = this.props;

    if (hasError) {
      return fallback || this.renderDefaultFallback();
    }

    return children;
  }
}

/**
 * Wrapper component that uses hooks and passes them to the class component
 * Error boundaries must be class components, but we can use hooks in a wrapper
 */
function ErrorBoundaryWrapper(props: Props) {
  const {pressed} = useGenericButtonPressedEvent();

  const handleButtonPressed = (buttonName: string, screenName: string) => {
    pressed({
      button_name: buttonName,
      screen_name: screenName,
    });
  };

  return <ErrorBoundary {...props} onButtonPressed={handleButtonPressed} />;
}

export default ErrorBoundaryWrapper;
